import type { DeliveryInstructionsSaveFailure } from '../types/account';
import { mutateSessionStorage, withSessionStorage } from './sessionStorageAccess';

const PENDING_ADDRESS_SAVE_RETRY_KEY = 'pendingAddressSaveRetry';

export type PendingAddressSaveRetry = DeliveryInstructionsSaveFailure & {
    cartId: string;
};

/**
 * Stores a failed sign-in delivery instructions save so checkout can ask the shopper to save the address again.
 * Kept in `sessionStorage` because sign-in can finish with a full page load before checkout renders.
 */
export function persistPendingAddressSaveRetry(
    cartId: string | undefined,
    failure: DeliveryInstructionsSaveFailure | undefined
): void {
    if (!cartId || !failure) {
        return;
    }

    const pending: PendingAddressSaveRetry = {
        cartId,
        ...(failure.addressId ? { addressId: failure.addressId } : {}),
    };
    mutateSessionStorage(storage => storage.setItem(PENDING_ADDRESS_SAVE_RETRY_KEY, JSON.stringify(pending)));
}

export function readPendingAddressSaveRetry(cartId: string | undefined): PendingAddressSaveRetry | null {
    if (!cartId) {
        return null;
    }

    return withSessionStorage<PendingAddressSaveRetry | null>({
        fallback: null,
        read: storage => {
            const stored = storage.getItem(PENDING_ADDRESS_SAVE_RETRY_KEY);
            if (!stored) {
                return null;
            }

            const parsed = JSON.parse(stored) as Partial<PendingAddressSaveRetry>;
            if (parsed.cartId !== cartId) {
                return null;
            }

            return {
                cartId,
                ...(typeof parsed.addressId === 'string' && parsed.addressId ? { addressId: parsed.addressId } : {}),
            };
        },
    });
}

export function clearPendingAddressSaveRetry(): void {
    mutateSessionStorage(storage => storage.removeItem(PENDING_ADDRESS_SAVE_RETRY_KEY));
}
